import { validatePolish } from '@rent/ui/helpers'
import { TextLink } from 'components/shared'
import { ExternalLinks, GeneralRoute, MAX_TEXT_LENGTH, OrderStatus, PHONE_REGEXP_PL } from 'consts'
import {
  CustomerPersonalDataConsentsFields,
  CustomerPersonalDataFields,
  ICustomerOrder,
  OnboardingCustomerFieldsPropertiesType,
} from 'models'
import { z } from 'zod'

import { MarketingConsents } from './hooks'

export const validationSchema = (formatMessage: ({ id }: { id: string }) => string, isLoan: boolean, freeInstallmentQualified: boolean) =>
  z
    .object({
      [CustomerPersonalDataFields.firstName]: z
        .string()
        .trim()
        .min(1, { message: formatMessage({ id: 'notification.required_field' }) }),

      [CustomerPersonalDataFields.lastName]: z
        .string()
        .trim()
        .min(1, { message: formatMessage({ id: 'notification.required_field' }) }),

      [CustomerPersonalDataFields.nip]: z
        .string()
        .min(1, { message: formatMessage({ id: 'notification.required_field' }) })
        .refine(value => validatePolish.nip(value), { message: formatMessage({ id: 'notification.nip_invalid' }) }),

      [CustomerPersonalDataFields.buyerEmail]: z.string().min(1, { message: formatMessage({ id: 'notification.required_field' }) }),

      [CustomerPersonalDataFields.buyerPhone]: z.string().min(1, { message: formatMessage({ id: 'notification.required_field' }) }),

      // helper
      editableFields: z.object({
        [CustomerPersonalDataFields.firstName]: z.boolean(),
        [CustomerPersonalDataFields.lastName]: z.boolean(),
        [CustomerPersonalDataFields.buyerEmail]: z.boolean(),
        [CustomerPersonalDataFields.buyerPhone]: z.boolean(),
      }),
      // END helper

      // checkboxes
      [CustomerPersonalDataConsentsFields.selectAll]: z.boolean(),

      [CustomerPersonalDataConsentsFields.agreementRodo]: z.boolean().refine(val => val, {
        message: formatMessage({ id: 'notification.required_field' }),
      }),

      [CustomerPersonalDataConsentsFields.agreementStatute]: z.boolean().refine(val => val, {
        message: formatMessage({ id: 'notification.required_field' }),
      }),

      [CustomerPersonalDataConsentsFields.agreementKrd]: z.boolean().refine(val => val, {
        message: formatMessage({ id: 'notification.required_field' }),
      }),
      ...(freeInstallmentQualified
        ? {
      [CustomerPersonalDataConsentsFields.agreementFreeInstallment]: z.boolean().refine(val => val, {
        message: formatMessage({ id: 'notification.required_field' }),
      })} : {}),

      [CustomerPersonalDataConsentsFields.agreementEmailCommunication]: z.boolean(),
      [CustomerPersonalDataConsentsFields.agreementPhoneCommunication]: z.boolean(),
      ...(isLoan
        ? {
            [CustomerPersonalDataConsentsFields.loanAgreementEmailCommunication]: z.boolean(),
            [CustomerPersonalDataConsentsFields.loanAgreementPhoneCommunication]: z.boolean(),
            [CustomerPersonalDataConsentsFields.loan2AgreementEmailCommunication]: z.boolean(),
            [CustomerPersonalDataConsentsFields.loan2AgreementPhoneCommunication]: z.boolean(),
          }
        : {}),
    })
    .superRefine(({ firstName, lastName, buyerEmail, buyerPhone, editableFields }, ctx) => {
      if (!editableFields[CustomerPersonalDataFields.firstName] && firstName?.trim().length > MAX_TEXT_LENGTH['SMALL']) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({ id: 'notification.max_length_small' }),
          path: [CustomerPersonalDataFields.firstName],
        })
      }
      if (!editableFields[CustomerPersonalDataFields.lastName] && lastName?.trim().length > MAX_TEXT_LENGTH['SMALL']) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({ id: 'notification.max_length_small' }),
          path: [CustomerPersonalDataFields.lastName],
        })
      }
      if (!editableFields[CustomerPersonalDataFields.buyerEmail] && !z.string().email().safeParse(buyerEmail).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({ id: 'notification.email_invalid' }),
          path: [CustomerPersonalDataFields.buyerEmail],
        })
      }
      if (!editableFields[CustomerPersonalDataFields.buyerPhone] && !PHONE_REGEXP_PL.test(buyerPhone)) {
        ctx.addIssue({
          path: [CustomerPersonalDataFields.buyerPhone],
          code: z.ZodIssueCode.custom,
          message: formatMessage({ id: 'notification.phoneNumber_invalid_pl' }),
        })
      }
    })

export type ValidationSchema = z.infer<ReturnType<typeof validationSchema>>

export const customerPersonalDataFieldsProperties = (
  orderStatus: OrderStatus | '',
  initialNip: boolean | undefined,
): OnboardingCustomerFieldsPropertiesType => ({
  [CustomerPersonalDataFields.firstName]: {
    type: 'text',
    inputProps: { fullWidth: true },
  },
  [CustomerPersonalDataFields.lastName]: {
    type: 'text',
    inputProps: { fullWidth: true },
  },
  [CustomerPersonalDataFields.nip]: {
    type: 'nip',
    inputProps: { fullWidth: true, disabled: orderStatus === OrderStatus.PRE_ORDER || initialNip },
  },
  [CustomerPersonalDataFields.buyerEmail]: {
    type: 'email',
    inputProps: { fullWidth: true },
  },
  [CustomerPersonalDataFields.buyerPhone]: {
    type: 'phone',
    inputProps: { fullWidth: true },
  },
})

export const customerPersonalDataConsentsFieldsProperties = (
  marketingConsents: MarketingConsents,
  formatMessage: ({ id }: { id: string }) => string,
  isLoan: boolean,
  freeInstallmentQualified: boolean
) => ({
  [CustomerPersonalDataConsentsFields.agreementRodo]: {
    type: 'checkbox',
    required: true,
    customLabel: {
      id: `form.${CustomerPersonalDataConsentsFields.agreementRodo}_label`,
      values: {
        link: (
          <TextLink href={isLoan ? ExternalLinks.RODO_REVILO : ExternalLinks.RODO} id="rodo-link">
            {formatMessage({ id: 'rodo_info' })}
          </TextLink>
        ),
      },
    },
  },
  [CustomerPersonalDataConsentsFields.agreementStatute]: {
    type: 'checkbox',
    required: true,
    customLabel: {
      id: `form.${CustomerPersonalDataConsentsFields.agreementStatute}_label`,
      values: {
        link: (
          <TextLink href={`/${GeneralRoute.TERMS}`} id="terms-link">
            {formatMessage({ id: 'statute_rentPay' })}
          </TextLink>
        ),
      },
    },
  },
  [CustomerPersonalDataConsentsFields.agreementKrd]: {
    type: 'checkbox-accordion',
    required: true,
    customLabelExpanded: {
      id: isLoan
        ? `form.${CustomerPersonalDataConsentsFields.agreementKrd}${isLoan ? 'Loan' : ''}_label_expanded`
        : `form.${CustomerPersonalDataConsentsFields.agreementKrd}_label_expanded`,
      values: {},
    },
  },
  ...(freeInstallmentQualified
    ? {
    [CustomerPersonalDataConsentsFields.agreementFreeInstallment]: {
      type: 'checkbox-accordion',
      required: true,
      customLabelExpanded: {
        id: `form.${CustomerPersonalDataConsentsFields.agreementFreeInstallment}_label_expanded`,
        values: {
          link: (
            <TextLink href={ ExternalLinks.PROMOTION_STATUTE } id="promotion-link">
              {formatMessage({ id: 'statute_promotion' })}
            </TextLink>
          ),
        },
      },
    }}
  : {}),
  [CustomerPersonalDataConsentsFields.agreementEmailCommunication]: {
    type: 'checkbox-accordion',
    disabled: marketingConsents.electronicProcessingAccepted,
  },
  [CustomerPersonalDataConsentsFields.agreementPhoneCommunication]: {
    type: 'checkbox-accordion',
    disabled: marketingConsents.phoneProcessingAccepted,
  },
  ...(isLoan
    ? {
        [CustomerPersonalDataConsentsFields.loanAgreementEmailCommunication]: {
          type: 'checkbox-accordion',
          disabled: marketingConsents.loanElectronicProcessingAccepted,
        },
      }
    : {}),
  ...(isLoan
    ? {
        [CustomerPersonalDataConsentsFields.loanAgreementPhoneCommunication]: {
          type: 'checkbox-accordion',
          disabled: marketingConsents.loanPhoneProcessingAccepted,
        },
      }
    : {}),
  ...(isLoan
    ? {
        [CustomerPersonalDataConsentsFields.loan2AgreementEmailCommunication]: {
          type: 'checkbox-accordion',
          disabled: marketingConsents.loan2ElectronicProcessingAccepted,
        },
      }
    : {}),
  ...(isLoan
    ? {
        [CustomerPersonalDataConsentsFields.loan2AgreementPhoneCommunication]: {
          type: 'checkbox-accordion',
          disabled: marketingConsents.loan2PhoneProcessingAccepted,
        },
      }
    : {}),
})

export const formInitValues = (data: ICustomerOrder | null | undefined) => ({
  firstName: data?.firstName ?? '',
  lastName: data?.lastName ?? '',
  buyerEmail: data?.buyerEmail ?? '',
  buyerPhone: data?.buyerPhone ?? '',
  nip: data?.nip ? data?.nip.toString() : '',
  selectAll: false,
  agreementRodo: false,
  agreementStatute: false,
  agreementKrd: false,
  agreementEmailCommunication: false,
  agreementPhoneCommunication: false,
  loanAgreementEmailCommunication: false,
  loanAgreementPhoneCommunication: false,
  loan2AgreementEmailCommunication: false,
  loan2AgreementPhoneCommunication: false,

  editableFields: {
    [CustomerPersonalDataFields.firstName]: false,
    [CustomerPersonalDataFields.lastName]: false,
    [CustomerPersonalDataFields.buyerEmail]: false,
    [CustomerPersonalDataFields.buyerPhone]: false,
  },
})
